function parseRawImageData(rawData) {
  if (Array.isArray(rawData) && rawData.length > 0) {
    const first = rawData[0];
    if (typeof first === 'string') {
      if (first.trim().startsWith('[') || first.includes('&quot;')) {
        try {
          const cleaned = first.replace(/&quot;/g, '"').replace(/\\"/g, '"');
          const parsed = JSON.parse(cleaned);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return rawData;
        }
      }
      return rawData;
    }
    return rawData;
  }
  if (typeof rawData === 'string') {
    try {
      const cleaned = rawData
        .replace(/^\['/, '[')
        .replace(/'\]$/, ']')
        .replace(/&quot;/g, '"')
        .replace(/\\"/g, '"');
      const parsed = JSON.parse(cleaned);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      try {
        return JSON.parse(rawData);
      } catch {
        return [];
      }
    }
  }
  return [];
}

function normalizePoiFileName(img, maptriksId) {
  if (!img || typeof img !== 'string') return null;
  if (maptriksId && img.startsWith(`${maptriksId}_`)) {
    return /\.(jpg|jpeg|png)$/i.test(img) ? img : `${img.replace(/\.(jpeg|png)$/i, '')}.jpg`;
  }
  const datePattern = /^\d{4}[-_]\d{2}[-_]\d{2}/;
  if (datePattern.test(img)) {
    if (/\.(jpg|jpeg|png)$/i.test(img) || /(jpeg|jpg|png)$/i.test(img)) return img;
    return `${img}.jpg`;
  }
  if (maptriksId) {
    let cleanImg = img.replace(/_[a-zA-Z0-9]+(jpeg|png)$/i, '');
    if (!/\.(jpg|jpeg|png)$/i.test(cleanImg)) {
      cleanImg = `${cleanImg}.jpg`;
    } else if (/\.(jpeg|png)$/i.test(cleanImg)) {
      cleanImg = cleanImg.replace(/\.(jpeg|png)$/i, '.jpg');
    }
    return `${maptriksId}_${cleanImg}`;
  }
  return img;
}

function extractTarihFromFileName(img) {
  const tarihRegex = /(\d{4}-\d{2}-\d{2})_(\d{2})\.(\d{2})\.(\d{2})/;
  const match = img && String(img).match(tarihRegex);
  if (match) {
    const [, tarih, saat, dakika, saniye] = match;
    return `${tarih} ${saat}:${dakika}:${saniye}`;
  }
  return '';
}

const BASE_URL = 'https://dosya.atmturkiye.com/atmpoint/poifoto';

/**
 * imgproxy dönüşüm ekini kesip nesnenin ORİJİNALİNE işaret eden URL'i döndürür.
 *
 * Görüntüler nginx üzerinden imgproxy ile küçültülerek sunuluyor; URL şeması
 * `<nesne yolu>/rs:fill:1100:650/q:70` biçiminde. nginx tarafındaki kural
 * (`/docker-files/nginx/nginx.conf`, "ImgProxy Resize" bloğu):
 *     ^/(.+?)/((rs|fill|fit|size|q|w|h):.+)$
 * Buradaki dönüşüm anahtarları birebir aynı tutulur; ilk eşleşen segmentten
 * sonrası kesilince geriye S3/MinIO nesne yolu kalır.
 *
 * Dosya adı uzantısına BAKMAZ: nesnelerin çoğunda uzantı yok
 * (ör. `2026-09-01_16.59.52_289_n5QFtWO6ajjpeg`), uzantı arayan bir kalıp
 * bu adlarda sessizce eşleşmezdi.
 */
export function getOriginalPoiUrl(url) {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/\/(?:rs|fill|fit|size|q|w|h):.*$/, '');
}

/**
 * İndirilecek dosya için güvenli ad: uzantısı yoksa ekler.
 *
 * Orijinal nesneler MinIO'dan uzantısız ve `application/octet-stream` ile
 * geliyor (ölçüldü). Uzantı eklenmezse inen dosya hiçbir görüntüleyicide
 * açılmaz. Ad `...jpeg`/`...png` ile bitiyorsa (noktasız) nokta eklenir,
 * hiçbir ipucu yoksa `.jpg` varsayılır.
 */
export function poiDosyaAdiUzantili(ad, contentType) {
  const temel = String(ad || 'resim').split('/').pop();
  if (/\.(jpg|jpeg|png|webp|gif)$/i.test(temel)) return temel;

  const noktasiz = temel.match(/(jpeg|jpg|png|webp|gif)$/i);
  if (noktasiz) {
    return `${temel.slice(0, -noktasiz[1].length)}.${noktasiz[1].toLowerCase()}`;
  }

  const ctUzanti = /image\/(jpeg|jpg|png|webp|gif)/i.exec(contentType || '');
  return `${temel}.${ctUzanti ? ctUzanti[1].toLowerCase().replace('jpeg', 'jpg') : 'jpg'}`;
}

export function getImageUrls(rawData, maptriksId, options = {}) {
  const {
    isHighQuality = false,
    lowQualitySize = '300:200',
    includeTarih = false,
    includeTitle = false,
    includeFilename = false,
    useNormalizedId = false,
  } = options;

  try {
    const transformParams = isHighQuality
      ? '/rs:fill:1100:650/q:70'
      : `/rs:fill:${lowQualitySize}/q:70`;

    const parsedArray = parseRawImageData(rawData);
    if (!Array.isArray(parsedArray) || parsedArray.length === 0) return [];

    const result = [];
    for (let index = 0; index < parsedArray.length; index++) {
      const img = parsedArray[index];
      if (!img || typeof img !== 'string') continue;

      const normalizedImg = normalizePoiFileName(img, maptriksId);
      if (!normalizedImg) continue;

      const datePattern = /^\d{4}[-_]\d{2}[-_]\d{2}/;
      const pathId = datePattern.test(normalizedImg) || (normalizedImg.split('_')[0] === String(maptriksId))
        ? maptriksId
        : maptriksId;
      const url = `${BASE_URL}/${pathId}/${normalizedImg}${transformParams}`;

      // `url` önizleme (küçültülmüş) sürüm; `originalUrl` indirme içindir.
      // Bu alan eskiden hiç üretilmiyordu, ImageModal ise onu okuyup
      // bulamayınca küçültülmüş dosyayı indiriyordu (25.08 md, madde 5).
      const item = {
        id: useNormalizedId ? normalizedImg : img,
        url,
        originalUrl: `${BASE_URL}/${pathId}/${normalizedImg}`,
      };
      if (includeTarih) item.tarih = extractTarihFromFileName(img);
      if (includeTitle) item.title = `Lokasyon Resmi ${index + 1}`;
      if (includeFilename) item.filename = normalizedImg;
      result.push(item);
    }
    return result;
  } catch (err) {
    console.error('getImageUrls error:', err);
    return [];
  }
}

export function getImageUrlStrings(rawData, maptriksId, isHighQuality = false) {
  const items = getImageUrls(rawData, maptriksId, {
    isHighQuality,
    lowQualitySize: '300:200',
  });
  return items.map((item) => item.url);
}
